import {CompDisplayFieldLabels, KeyMap, SalesforceField} from './types';
import {getCollection, getDocument} from '@/services/FirestoreService';

let keyMap : KeyMap[] | null = null;
const fieldConfigByDocument = new Map<string, SalesforceField>();

const getKeyMap = async (): Promise<KeyMap[]> => {
  if (keyMap) {
    return keyMap;
  } else {
    try {
      const keyMapCollection = await getCollection('salesforce-key-map');
      keyMap = keyMapCollection.docs.map((doc) => doc.data()) as KeyMap[];
      return keyMap || [];
    } catch (error) {
      throw new Error('Key map not found or could not be retrieved.');
    }
  }
};

const getKeyMapEntry = async (key: string): Promise<KeyMap | undefined> => {
  if (!keyMap) {
    try {
      await getKeyMap();
    } catch (error) {
      throw new Error(`Key map not found: ${key}`);
    }
  }
  try {
    const keyMapEntry = keyMap?.find((entry) =>
      entry.firestoreKey === key || entry.salesforceKey === key || entry.label === key);
    if (!keyMapEntry) {
      throw new Error(`Key map entry not found: ${key}`);
    }
    return keyMapEntry;
  } catch (error) {
    throw new Error(`Key map entry not found: ${key}`);
  }
};

const getFieldConfigForDocument = async (documentName: string): Promise<SalesforceField | undefined> => {
  const cached = fieldConfigByDocument.get(documentName);
  if (cached) {
    return cached;
  }
  const config = await getDocument('salesforce-fields', documentName) as SalesforceField | undefined;
  if (config) {
    fieldConfigByDocument.set(documentName, config);
  }
  return config;
};

const getStandardFieldOrderForDocument = async (document: string): Promise<string[]> => {
  try {
    const config = await getFieldConfigForDocument(document);
    const standardFieldOrder = config?.standardFieldOrder || [];
    if (standardFieldOrder.length > 0) {
      return standardFieldOrder;
    }
    throw new Error(`Standard field order was not found on document.`);
  } catch (error) {
    throw new Error(`Standard field order not found for document: ${document}`);
  }
};

const getKeyMapBySalesforceKey = async (): Promise<Map<string, KeyMap>> => {
  const map = await getKeyMap();
  const bySalesforceKey = new Map<string, KeyMap>();
  for (const entry of map) {
    if (entry.salesforceKey && !bySalesforceKey.has(entry.salesforceKey)) {
      bySalesforceKey.set(entry.salesforceKey, entry);
    }
  }
  return bySalesforceKey;
};

const labelsForSalesforceKeys = (keys: string[], bySalesforceKey: Map<string, KeyMap>): string[] => {
  const labels: string[] = [];
  for (const key of keys) {
    const entry = bySalesforceKey.get(key);
    if (!entry || entry.isMetadata) continue;
    labels.push(entry.label);
  }
  return labels;
};

const getDisplayFieldLabelsForDocument = async (documentName: string): Promise<string[]> => {
  try {
    const order = await getStandardFieldOrderForDocument(documentName);
    return labelsForSalesforceKeys(order, await getKeyMapBySalesforceKey());
  } catch (error) {
    return [];
  }
};

const getCompGroupDisplayFieldLabels = async (
  documentName: string,
  groupName: string,
): Promise<CompDisplayFieldLabels[]> => {
  try {
    const config = await getFieldConfigForDocument(documentName);
    const group = config?.compGroups?.find((candidate) => candidate.name === groupName);
    if (!group?.comps?.length) {
      return [];
    }
    const bySalesforceKey = await getKeyMapBySalesforceKey();
    return group.comps
      .map((comp) => ({
        compName: comp.name,
        labels: labelsForSalesforceKeys(comp.fields || [], bySalesforceKey),
      }))
      .filter((comp) => comp.labels.length > 0);
  } catch (error) {
    return [];
  }
};

export {
  getKeyMap,
  getKeyMapEntry,
  getStandardFieldOrderForDocument,
  getDisplayFieldLabelsForDocument,
  getCompGroupDisplayFieldLabels,
};
